import KrpanoBasic from '@components/Krpano/KrpanoBasic';
import { I_NadirConfig, I_Scene } from '@containers/Home/types';
import React, {
  MouseEventHandler,
  TouchEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BeforeAfterSliderWrapper } from './styled';
import { objectType } from '@type/customType';

// eslint-disable-next-line no-shadow
enum MODE_CURSOR {
  MOVE = 'move',
  DEFAULT = 'default',
}

export function isIntersectionObserverSupport() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.IntersectionObserver);
}

export interface Image {
  id?: string | number;
  imageUrl: string;
}

interface Props {
  scenes?: (I_Scene & { urlTour: string })[];
  classNames?: string;
  onVisible?: () => void;
  delimiterColor?: string;
  withResizeFeel?: boolean;
  firstPane: React.ReactNode;
  nadirConfig?: I_NadirConfig;
  mode: 'split' | 'slide' | '';
}

function useInit(
  updateContainerWidth: () => void,
  onMouseUpHandler: () => void,
) {
  useEffect(() => {
    updateContainerWidth();
    document.addEventListener('click', onMouseUpHandler);
    return () => {
      document.removeEventListener('click', onMouseUpHandler);
    };
  }, [onMouseUpHandler, updateContainerWidth]);
}

function useResizeFeel(callback: () => void, withResizeFeel?: boolean) {
  useEffect(() => {
    if (withResizeFeel) {
      window.addEventListener('resize', callback);
    }

    return () => {
      window.removeEventListener('resize', callback);
    };
  }, [callback, withResizeFeel]);
}

export default function BeforeAfterSlider({
  scenes = [],
  firstPane,
  onVisible,
  classNames,
  nadirConfig,
  delimiterColor,
  withResizeFeel = true,
  mode = 'slide',
}: Props) {
  const refContainer = useRef<HTMLDivElement>(null);
  const stateTemporary = useRef<objectType | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [panesPosition, setPanesPosition] = useState<
    {
      width: number;
      left: number;
    }[]
  >([]);
  const [sliderMode, setSliderMode] = useState<MODE_CURSOR>(
    MODE_CURSOR.DEFAULT,
  );
  const [containerPosition, setContainerPosition] = useState({
    top: 0,
    left: 0,
  });

  const firstPanePosition = useMemo(
    () =>
      mode === 'split'
        ? {
            width:
              100 -
              (panesPosition || [])?.reduce((a, b) => a + b?.width || 0, 0),
            left: panesPosition?.reduce((a, b) => a + b.width, 0),
          }
        : {
            width: '100%',
            left: '0',
          },
    [mode, panesPosition],
  );

  /**
   * Observer start
   */
  const observerVisiblePersent = 0.95;
  const observerOptions = {
    threshold: [0.0, observerVisiblePersent],
  };
  const observerCallback = (entries: IntersectionObserverEntry[]) => {
    if (!observer || !onVisible) return;
    entries.forEach(entry => {
      if (entry.intersectionRatio > observerVisiblePersent) {
        observer.disconnect();
        onVisible();
      }
    });
  };
  const [observer] = useState(
    onVisible && isIntersectionObserverSupport()
      ? new IntersectionObserver(observerCallback, observerOptions)
      : null,
  );

  useEffect(() => {
    if (observer) observer.observe(refContainer.current as HTMLDivElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observer]);

  /**
   * Observer end
   */

  useEffect(() => {
    if (!containerWidth) return;
    const positions: { width: number; left: number }[] = [];
    const widthAverage = Number(100 / (scenes.length + 1));
    for (let index = 0; index < scenes.length; index += 1) {
      if (index === 0) positions.push({ width: widthAverage, left: 0 });
      else {
        const left = widthAverage * index;
        positions.push({ width: widthAverage, left });
      }
    }
    setPanesPosition(positions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth, scenes]);

  const updateContainerWidth = () => {
    if (!refContainer.current) return;
    const width = refContainer.current.offsetWidth;
    setContainerWidth(width);
  };

  const onMouseUpHandler = () => {
    setSliderMode(MODE_CURSOR.DEFAULT);
  };

  useInit(updateContainerWidth, onMouseUpHandler);

  const delimiterIconStyles = {
    backgroundColor: delimiterColor,
  };
  const delimiterPositionStyle = {
    backgroundColor: delimiterColor,
    right: 0,
  };

  const updateContainerPosition = () => {
    if (!refContainer.current) return;
    const containerCoords = refContainer.current.getBoundingClientRect();

    setContainerPosition({
      top: containerCoords.top + (window as any).pageYOffset,
      left: containerCoords.left + (window as any).pageXOffset,
    });
  };

  const onMouseDownHandler = item => {
    updateContainerPosition();
    setSliderMode(MODE_CURSOR.MOVE);
    if (!stateTemporary.current) stateTemporary.current = [];
    stateTemporary.current.itemActive = item;
  };

  const onMouseMoveHandler: MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    onMoveHandler(e);
  };

  const onTouchMoveHandler: TouchEventHandler<HTMLDivElement> = e => {
    onMoveHandler(e.touches[0]);
  };

  const onMoveHandler = (e: React.Touch | React.MouseEvent) => {
    if (sliderMode === MODE_CURSOR.MOVE) {
      if (!containerWidth) return;
      const X = e.pageX - containerPosition.left;
      const newPosition = (X / containerWidth) * 100;
      if (stateTemporary.current?.itemActive === panesPosition.length - 1)
        setPanesPosition(prev =>
          prev.map((item, idx) => {
            if (idx === stateTemporary.current?.itemActive) {
              const newItem = {
                width:
                  newPosition -
                  (panesPosition
                    .map(el => el.width || 0)
                    .reduce((a, b) => a + b, 0) -
                    item.width),
                left: item.left,
              };
              if (newItem.width > 5) return newItem;
            }
            return item;
          }),
        );
      else
        setPanesPosition(prev =>
          prev.map((item, idx) => {
            if (
              stateTemporary.current &&
              idx === stateTemporary.current?.itemActive
            ) {
              stateTemporary.current.totalWidthCurrentAndNextItem =
                item.width + prev[idx + 1].width;
              stateTemporary.current.totalWidthItemsLtCurrent = prev
                .filter((_item, i) => i < stateTemporary.current?.itemActive)
                .map(el => el.width || 0)
                .reduce((a, b) => a + b, 0);
              stateTemporary.current.currentWidthOld = item.width;
              stateTemporary.current.currentWidthNew =
                stateTemporary.current.itemActive === 0
                  ? newPosition
                  : newPosition -
                    stateTemporary.current.totalWidthItemsLtCurrent;
              const newItem = {
                width: stateTemporary.current.currentWidthNew,
                left: item.left,
              };
              stateTemporary.current.isUpdateNextItem = false;
              if (
                newItem.width > 5 &&
                newItem.width <
                  stateTemporary.current.totalWidthCurrentAndNextItem - 5
              ) {
                stateTemporary.current.isUpdateNextItem = true;
                return newItem;
              }
            }
            if (
              stateTemporary.current &&
              idx === stateTemporary.current.itemActive + 1
            ) {
              const newWidth =
                item.width +
                stateTemporary.current.currentWidthOld -
                stateTemporary.current.currentWidthNew;
              const newItem = {
                width: newWidth,
                left:
                  stateTemporary.current.totalWidthItemsLtCurrent +
                  stateTemporary.current.currentWidthNew,
              };
              if (stateTemporary.current.isUpdateNextItem) return newItem;
            }
            return item;
          }),
        );
    }
  };

  useResizeFeel(updateContainerWidth, withResizeFeel);

  return (
    <BeforeAfterSliderWrapper
      ref={refContainer}
      className={classNames}
      onMouseMove={onMouseMoveHandler}
      onTouchMove={onTouchMoveHandler}
    >
      {panesPosition && Boolean(containerWidth) && (
        <>
          <div
            className="--pane --first-photo-container"
            style={{
              width: `${firstPanePosition.width}%`,
              left: `${firstPanePosition.left}%`,
              zIndex: 0,
            }}
          >
            {firstPane}
          </div>
          {scenes.map((item, index) => (
            <div
              key={item.id}
              className="--pane --second-photo-container"
              style={{
                width: `${panesPosition[index]?.width || 0}%`,
                left: `${panesPosition[index]?.left || 0}%`,
                zIndex: scenes.length - index,
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <KrpanoBasic
                  urlTour={item.urlTour || ''}
                  scene={item || undefined}
                  krpanoId={`krpanoView_${index}`}
                  divId={`krpanoContainer_${index}`}
                  styleContainer={{
                    height: '100%',
                    position: 'absolute',
                    left:
                      mode === 'slide'
                        ? `calc(-1vw * ${panesPosition[index]?.left})`
                        : '0',
                    top: 0,
                    width: mode === 'slide' ? '100vw' : '100%',
                  }}
                  nadirConfig={nadirConfig}
                />
              </div>
              <div className="--delimiter" style={delimiterPositionStyle}>
                {mode === 'slide' && (
                  <div>
                    <div
                      className="--delimiter-icon"
                      onMouseDown={() => onMouseDownHandler(index)}
                      onTouchStart={() => onMouseDownHandler(index)}
                      style={delimiterIconStyles}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </BeforeAfterSliderWrapper>
  );
}
